import { Link } from "react-router-dom";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import {
  hasPermission,
  PORTAL_BUNDLE_RC26_FILE_COUNT,
  PORTAL_BUNDLE_RC26_RELEASE_ID,
  type Role,
} from "@vital/shared";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/Card";
import { MetricCard } from "../components/MetricCard";
import { OpsEmptyState } from "../components/ops-display";
import { InspectPanel } from "../components/InspectPanel";
import { StickyFormActions } from "../components/StickyFormActions";
import {
  ComposerHeader,
  ComposerPanel,
  ComposerSection,
  ComposerWorkspace,
} from "../components/ComposerChrome";
import {
  BundleJobFilterBar,
  BundleJobInspectSummary,
  BundleJobInventoryList,
  BundleTemplateFilterBar,
  BundleTemplateInspectSummary,
  BundleTemplateInventoryList,
} from "../components/bundle-inventory";
import { api, ApiError, type PortalBundleJob } from "../lib/api";
import { cmsStrings } from "../terminology";
import {
  BUNDLE_AUTHORITY_NOTE,
  filterAndSortJobs,
  filterAndSortTemplates,
  frozenAuthorityForRelease,
  releaseIdFromBundleFilename,
  templateReleaseHashConflict,
  toBundleJobRow,
  verifyDeclarationDefaults,
  type BundleJobRow,
  type BundleTemplateRow,
  type JobSortKey,
  type TemplateSortKey,
} from "../lib/bundle-ops";

type Template = BundleTemplateRow;

type AdminSite = {
  id: string;
  code: string;
  name: string;
  location: string | null;
  active: boolean;
  codeLocked: boolean;
};

async function copyText(text: string): Promise<void> {
  await navigator.clipboard.writeText(text);
}

function formatKabul(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("fa-AF", { timeZone: "Asia/Kabul" });
}

export function PortalBundlesPage() {
  const { user } = useAuth();
  const role = user?.role as Role | undefined;
  const canGenerate = role ? hasPermission(role, "portal.bundle.generate") : false;
  const canManage = role ? hasPermission(role, "portal.bundle.manage") : false;

  const ui = cmsStrings.bundleUi;
  const c = cmsStrings.bundleComposer;

  const [templates, setTemplates] = useState<Template[]>([]);
  const [sites, setSites] = useState<AdminSite[]>([]);
  const [history, setHistory] = useState<PortalBundleJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [historySiteFilter, setHistorySiteFilter] = useState("");

  const [templateQuery, setTemplateQuery] = useState("");
  const [templateStatus, setTemplateStatus] = useState("all");
  const [templateSort, setTemplateSort] = useState<TemplateSortKey>("release");
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  const [jobQuery, setJobQuery] = useState("");
  const [jobStatus, setJobStatus] = useState("all");
  const [jobSort, setJobSort] = useState<JobSortKey>("requested");
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  const [importFile, setImportFile] = useState<File | null>(null);
  const [importReleaseId, setImportReleaseId] = useState(PORTAL_BUNDLE_RC26_RELEASE_ID);
  const [importNotes, setImportNotes] = useState("");
  const [importing, setImporting] = useState(false);

  const [verifyTarget, setVerifyTarget] = useState<Template | null>(null);
  const [verifyReleaseId, setVerifyReleaseId] = useState(PORTAL_BUNDLE_RC26_RELEASE_ID);
  const [verifySha256, setVerifySha256] = useState("");
  const [verifyFileCount, setVerifyFileCount] = useState(PORTAL_BUNDLE_RC26_FILE_COUNT);
  const [verifying, setVerifying] = useState(false);

  const [generateSiteId, setGenerateSiteId] = useState("");
  const [generating, setGenerating] = useState(false);
  const [lastCompletedJob, setLastCompletedJob] = useState<PortalBundleJob | null>(null);

  const [revokeTarget, setRevokeTarget] = useState<PortalBundleJob | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const verifiedDefault = useMemo(
    () =>
      templates.find((t) => t.status === "verified" && t.isDefault) ??
      templates.find((t) => t.status === "verified"),
    [templates],
  );

  const generatableSites = useMemo(
    () => sites.filter((s) => s.active && s.codeLocked),
    [sites],
  );

  const unlockedActiveSites = useMemo(
    () => sites.filter((s) => s.active && !s.codeLocked),
    [sites],
  );

  const selectedSite = generatableSites.find((s) => s.id === generateSiteId) ?? null;

  const importFrozenRef = useMemo(
    () =>
      frozenAuthorityForRelease(importReleaseId) ??
      frozenAuthorityForRelease(PORTAL_BUNDLE_RC26_RELEASE_ID),
    [importReleaseId],
  );

  const filteredTemplates = useMemo(
    () =>
      filterAndSortTemplates(templates, {
        query: templateQuery,
        status: templateStatus,
        sort: templateSort,
      }),
    [templates, templateQuery, templateStatus, templateSort],
  );

  const jobRows = useMemo(() => history.map(toBundleJobRow), [history]);

  const filteredJobs = useMemo(
    () =>
      filterAndSortJobs(jobRows, {
        query: jobQuery,
        status: jobStatus,
        sort: jobSort,
      }),
    [jobRows, jobQuery, jobStatus, jobSort],
  );

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedTemplateId) ?? null,
    [templates, selectedTemplateId],
  );

  const selectedJobRow = useMemo(
    () =>
      filteredJobs.find((j) => j.id === selectedJobId) ??
      jobRows.find((j) => j.id === selectedJobId) ??
      null,
    [filteredJobs, jobRows, selectedJobId],
  );

  const verifiedCount = useMemo(
    () => templates.filter((t) => t.status === "verified").length,
    [templates],
  );
  const readyCount = useMemo(
    () => jobRows.filter((j) => j.status === "ready").length,
    [jobRows],
  );
  const failedCount = useMemo(
    () => jobRows.filter((j) => j.status === "failed").length,
    [jobRows],
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [templateRes, historyRes, siteList] = await Promise.all([
        api.listPortalBundleTemplates(),
        api.listPortalBundleHistory(historySiteFilter || undefined),
        api.listAllSites(),
      ]);
      setTemplates(templateRes.templates);
      setHistory(historyRes.history);
      setSites(siteList);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "بارگذاری ناموفق");
    } finally {
      setLoading(false);
    }
  }, [historySiteFilter]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (selectedTemplateId && !templates.some((t) => t.id === selectedTemplateId)) {
      setSelectedTemplateId(null);
    }
  }, [templates, selectedTemplateId]);

  useEffect(() => {
    if (selectedJobId && !history.some((j) => j.id === selectedJobId)) {
      setSelectedJobId(null);
    }
  }, [history, selectedJobId]);

  useEffect(() => {
    if (!selectedTemplateId && !selectedJobId) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      if (verifyTarget || revokeTarget) return;
      e.preventDefault();
      if (selectedJobId) setSelectedJobId(null);
      else setSelectedTemplateId(null);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedTemplateId, selectedJobId, verifyTarget, revokeTarget]);

  if (!canGenerate) {
    return (
      <>
        <PageHeader title="Bundle" />
        <p className="banner banner--error">{ui.noAccess}</p>
      </>
    );
  }

  async function handleImport(e: FormEvent) {
    e.preventDefault();
    if (!importFile) {
      setError("فایل ZIP انتخاب نشده است.");
      return;
    }
    setImporting(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await api.importPortalBundleTemplate(
        importFile,
        importReleaseId.trim(),
        importNotes.trim() || undefined,
      );
      setSuccess(`الگوی «${result.template.releaseId}» وارد شد — برای استفاده، تأیید کنید.`);
      setImportFile(null);
      setImportNotes("");
      await loadData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "ورود ناموفق");
    } finally {
      setImporting(false);
    }
  }

  function openVerifyModal(template: Template) {
    const declared = verifyDeclarationDefaults(template);
    setVerifyTarget(template);
    setVerifyReleaseId(declared.releaseId);
    setVerifySha256(declared.sha256);
    setVerifyFileCount(declared.fileCount);
  }

  async function handleVerify(e: FormEvent) {
    e.preventDefault();
    if (!verifyTarget) return;
    setVerifying(true);
    setError(null);
    setSuccess(null);
    try {
      await api.verifyPortalBundleTemplate(verifyTarget.id, {
        releaseId: verifyReleaseId.trim(),
        sha256: verifySha256.trim().toLowerCase(),
        fileCount: verifyFileCount,
      });
      setSuccess(`الگوی «${verifyReleaseId}» تأیید شد.`);
      setVerifyTarget(null);
      await loadData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "تأیید ناموفق");
    } finally {
      setVerifying(false);
    }
  }

  async function handleRetire(template: Template) {
    if (!window.confirm(`الگوی «${template.releaseId}» بازنشسته شود؟`)) return;
    setError(null);
    setSuccess(null);
    try {
      await api.retirePortalBundleTemplate(template.id);
      setSuccess(`الگوی «${template.releaseId}» بازنشسته شد.`);
      await loadData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "بازنشستگی ناموفق");
    }
  }

  async function handleSetDefault(template: Template) {
    setError(null);
    setSuccess(null);
    try {
      await api.setDefaultPortalBundleTemplate(template.id);
      setSuccess(ui.setDefaultDone);
      await loadData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "تنظیم پیش‌فرض ناموفق");
    }
  }

  async function handleGenerate(e: FormEvent) {
    e.preventDefault();
    if (!generateSiteId) {
      setError("سایت مقصد را انتخاب کنید.");
      return;
    }
    if (!verifiedDefault) {
      setError(ui.noDefaultWarning);
      return;
    }
    setGenerating(true);
    setError(null);
    setSuccess(null);
    setLastCompletedJob(null);
    try {
      const result = await api.createPortalBundleJob({
        siteId: generateSiteId,
        templateId: verifiedDefault.id,
      });
      if (result.job.status !== "ready" || !result.job.artifact) {
        setError("ساخت بسته کامل نشد — جزئیات را در تاریخچه ببینید.");
      } else {
        setLastCompletedJob(result.job);
        setSuccess(`بسته برای «${result.job.siteCode}» آماده شد.`);
        setSelectedJobId(result.job.id);
      }
      await loadData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "ساخت ناموفق");
    } finally {
      setGenerating(false);
    }
  }

  async function handleDownload(artifactId: string) {
    setDownloadingId(artifactId);
    setError(null);
    try {
      await api.downloadPortalBundleArtifact(artifactId);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "دانلود ناموفق");
    } finally {
      setDownloadingId(null);
    }
  }

  async function handleRevoke(e: FormEvent) {
    e.preventDefault();
    if (!revokeTarget?.artifact) return;
    setRevoking(true);
    setError(null);
    setSuccess(null);
    try {
      await api.revokePortalBundleArtifact(revokeTarget.artifact.id);
      setSuccess(`بسته «${revokeTarget.siteCode}» ابطال شد.`);
      setRevokeTarget(null);
      await loadData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "ابطال ناموفق");
    } finally {
      setRevoking(false);
    }
  }

  function canDownloadJob(job: PortalBundleJob | BundleJobRow): boolean {
    const status = job.status;
    const artifactId =
      "artifact" in job && job.artifact ? job.artifact.id : (job as BundleJobRow).artifactId;
    const expiresAt = job.expiresAt;
    if (status !== "ready" || !artifactId) return false;
    if (expiresAt && new Date() > new Date(expiresAt)) return false;
    return true;
  }

  function scrollToComposer() {
    document.getElementById("bundle-composer")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }

  function renderTemplateActions(row: Template): ReactNode {
    if (!canManage) return null;
    return (
      <>
        {row.status === "draft" && (
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => openVerifyModal(row)}
          >
            {ui.verify}
          </button>
        )}
        {row.status === "verified" && !row.isDefault && (
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => void handleSetDefault(row)}
          >
            {ui.setDefault}
          </button>
        )}
        {row.status !== "retired" && (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => void handleRetire(row)}
          >
            {ui.retire}
          </button>
        )}
      </>
    );
  }

  function renderJobActions(row: BundleJobRow): ReactNode {
    const apiJob = history.find((j) => j.id === row.id);
    return (
      <>
        {canDownloadJob(row) && row.artifactId && (
          <button
            type="button"
            className="btn btn--primary btn--sm"
            disabled={downloadingId === row.artifactId}
            onClick={() => void handleDownload(row.artifactId!)}
          >
            {downloadingId === row.artifactId ? "…" : ui.download}
          </button>
        )}
        {canManage && row.status === "ready" && apiJob?.artifact && (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => setRevokeTarget(apiJob)}
          >
            {ui.revoke}
          </button>
        )}
      </>
    );
  }

  function renderTemplateInspectFooter(row: Template) {
    const tools: ReactNode[] = [];
    if (canManage && row.status === "draft") {
      tools.push(
        <button
          key="verify"
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => openVerifyModal(row)}
        >
          {ui.verify}
        </button>,
      );
    }
    if (canManage && row.status === "verified" && !row.isDefault) {
      tools.push(
        <button
          key="default"
          type="button"
          className="btn btn--primary btn--sm"
          onClick={() => void handleSetDefault(row)}
        >
          {ui.setDefault}
        </button>,
      );
    }
    if (canManage && row.status !== "retired") {
      tools.push(
        <button
          key="retire"
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => void handleRetire(row)}
        >
          {ui.retire}
        </button>,
      );
    }
    tools.push(
      <button
        key="copy"
        type="button"
        className="btn btn--ghost btn--sm"
        onClick={() => void copyText(row.sha256)}
      >
        کپی SHA
      </button>,
    );
    return (
      <div className="inspect-panel__footer-inner">
        {tools.length > 0 ? <div className="inspect-panel__footer-tools">{tools}</div> : null}
      </div>
    );
  }

  function renderJobInspectFooter(row: BundleJobRow) {
    const tools: ReactNode[] = [];
    const primary: ReactNode[] = [];
    const apiJob = history.find((j) => j.id === row.id);

    if (canManage && row.status === "ready" && apiJob?.artifact) {
      tools.push(
        <button
          key="revoke"
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => setRevokeTarget(apiJob)}
        >
          {ui.revoke}
        </button>,
      );
    }

    if (canDownloadJob(row) && row.artifactId) {
      primary.push(
        <button
          key="download"
          type="button"
          className="btn btn--primary btn--sm"
          disabled={downloadingId === row.artifactId}
          onClick={() => void handleDownload(row.artifactId!)}
        >
          {downloadingId === row.artifactId ? "…" : ui.download}
        </button>,
      );
    } else {
      primary.push(
        <button
          key="generate"
          type="button"
          className="btn btn--primary btn--sm"
          onClick={scrollToComposer}
        >
          {ui.continueGenerate}
        </button>,
      );
    }

    return (
      <div className="inspect-panel__footer-inner">
        {tools.length > 0 ? <div className="inspect-panel__footer-tools">{tools}</div> : null}
        {primary.length > 0 ? <div className="inspect-panel__footer-primary">{primary}</div> : null}
      </div>
    );
  }

  const inventorySkeleton = (
    <div className="inventory-skeleton" aria-busy="true" aria-label={cmsStrings.shell.loading}>
      <div className="inventory-skeleton__row" style={{ width: "92%" }} />
      <div className="inventory-skeleton__row" style={{ width: "78%" }} />
      <div className="inventory-skeleton__row" style={{ width: "85%" }} />
      <div className="inventory-skeleton__row" style={{ width: "70%" }} />
      <div className="inventory-skeleton__row" style={{ width: "88%" }} />
    </div>
  );

  return (
    <>
      <PageHeader
        title="Bundle"
        actions={
          <a
            href="#bundle-composer"
            className="btn btn--primary btn--sm"
            onClick={() => {
              setGenerateSiteId("");
            }}
          >
            {c.generateCta}
          </a>
        }
      />

      <aside
        className="ops-safety-summary ops-safety-summary--privacy"
        role="note"
        data-vt-bundle-safety
      >
        <p className="ops-safety-summary__eyebrow">{ui.safetyEyebrow}</p>
        <ul className="ops-safety-summary__points">
          <li>{ui.safetyBulletZip}</li>
          <li>{ui.safetyBulletRollout}</li>
          <li>
            گروه ناوبری: <strong>انتشارها</strong>
          </li>
        </ul>
        <details className="ops-safety-disclosure">
          <summary>{ui.safetyDisclosure}</summary>
          <p>{BUNDLE_AUTHORITY_NOTE}</p>
        </details>
      </aside>

      {error && <p className="banner banner--error">{error}</p>}
      {success && <p className="banner banner--success">{success}</p>}

      <div className="metric-strip" data-vt-inventory-metrics aria-label="Bundle">
        <MetricCard label={ui.metricTemplates} value={templates.length} loading={loading} />
        <MetricCard label={ui.metricVerified} value={verifiedCount} loading={loading} />
        <MetricCard
          label={ui.metricReady}
          value={readyCount}
          loading={loading}
          sublabel={
            failedCount > 0 ? `${ui.metricFailedSublabel}: ${failedCount}` : undefined
          }
        />
      </div>

      <div className="page-stack page-stack--inventory">
        <div
          className={`content-ops-layout${selectedTemplate ? " content-ops-layout--inspect-open" : ""}`}
          data-vt-content-ops
          data-vt-inspect-open={selectedTemplate ? "true" : "false"}
        >
          <Card
            className="content-ops-layout__list inventory-surface"
            bodyClassName="card__body--flush"
          >
            <BundleTemplateFilterBar
              status={templateStatus}
              onStatusChange={setTemplateStatus}
              query={templateQuery}
              onQueryChange={setTemplateQuery}
              sort={templateSort}
              onSortChange={setTemplateSort}
            />
            {loading ? (
              inventorySkeleton
            ) : templates.length === 0 ? (
              <OpsEmptyState title="الگویی ثبت نشده است" />
            ) : filteredTemplates.length === 0 ? (
              <OpsEmptyState title="موردی در این فیلتر نیست" />
            ) : (
              <BundleTemplateInventoryList
                rows={filteredTemplates}
                selectedId={selectedTemplateId}
                onSelect={(id) =>
                  setSelectedTemplateId((current) => (current === id ? null : id))
                }
                renderActions={renderTemplateActions}
              />
            )}
          </Card>

          {selectedTemplate && (
            <InspectPanel
              title={ui.templateInspectTitle}
              className="content-inspect-panel"
              onClose={() => setSelectedTemplateId(null)}
              closeLabel={ui.close}
              footer={renderTemplateInspectFooter(selectedTemplate)}
            >
              <BundleTemplateInspectSummary item={selectedTemplate} />
            </InspectPanel>
          )}
        </div>

        <div
          className={`content-ops-layout${selectedJobRow ? " content-ops-layout--inspect-open" : ""}`}
          data-vt-content-ops
          data-vt-inspect-open={selectedJobRow ? "true" : "false"}
        >
          <Card
            className="content-ops-layout__list inventory-surface"
            bodyClassName="card__body--flush"
          >
            <BundleJobFilterBar
              status={jobStatus}
              onStatusChange={setJobStatus}
              query={jobQuery}
              onQueryChange={setJobQuery}
              sort={jobSort}
              onSortChange={setJobSort}
              siteFilter={historySiteFilter}
              onSiteFilterChange={setHistorySiteFilter}
              siteOptions={sites}
              onRefreshSiteFilter={() => void loadData()}
            />
            {loading ? (
              inventorySkeleton
            ) : history.length === 0 ? (
              <OpsEmptyState title="هنوز بسته‌ای ساخته نشده است" />
            ) : filteredJobs.length === 0 ? (
              <OpsEmptyState title="موردی در این فیلتر نیست" />
            ) : (
              <BundleJobInventoryList
                rows={filteredJobs}
                selectedId={selectedJobId}
                onSelect={(id) => setSelectedJobId((current) => (current === id ? null : id))}
                renderActions={renderJobActions}
              />
            )}
          </Card>

          {selectedJobRow && (
            <InspectPanel
              title={ui.jobInspectTitle}
              className="content-inspect-panel"
              onClose={() => setSelectedJobId(null)}
              closeLabel={ui.close}
              footer={renderJobInspectFooter(selectedJobRow)}
            >
              <BundleJobInspectSummary item={selectedJobRow} />
            </InspectPanel>
          )}
        </div>

        {lastCompletedJob?.artifact ? (
          <div className="ops-alert ops-alert--overlap" role="status">
            <div className="ops-alert__intro">
              <p className="ops-alert__title">{c.lastReadyTitle}</p>
              <p className="ops-alert__body">
                {c.lastReadyBody}
                {" · "}
                <bdi dir="ltr" className="ltr-isolate">
                  {lastCompletedJob.siteCode}
                </bdi>
                {" · "}
                <bdi dir="ltr" className="ltr-isolate">
                  {lastCompletedJob.artifact.generatedFilename}
                </bdi>
                {" · انقضا "}
                <bdi dir="ltr" className="ltr-isolate">
                  {formatKabul(lastCompletedJob.expiresAt)}
                </bdi>
              </p>
            </div>
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={() => void handleDownload(lastCompletedJob.artifact!.id)}
              disabled={downloadingId === lastCompletedJob.artifact.id}
            >
              {downloadingId === lastCompletedJob.artifact.id ? "در حال دانلود…" : "دانلود ZIP"}
            </button>
          </div>
        ) : null}

        <ComposerWorkspace id="bundle-composer" layout="solo" className="bundle-composer-workspace">
          {canManage ? (
            <ComposerPanel className="bundle-composer-panel">
              <ComposerHeader
                kind={cmsStrings.composerChrome.kindBundleImport}
                title={c.importTitle}
                statusLabel={cmsStrings.composerChrome.statusCreate}
                statusTone="info"
              />
              <form
                className="content-form content-form--in-card composer-form composer-form--bundle"
                onSubmit={handleImport}
              >
                <ComposerSection id="bundle-section-import" title={c.importTitle} bare>
                  <label>
                    <span>فایل ZIP</span>
                    <input
                      type="file"
                      accept=".zip,application/zip"
                      onChange={(e) => {
                        const file = e.target.files?.[0] ?? null;
                        setImportFile(file);
                        const fromName = file ? releaseIdFromBundleFilename(file.name) : null;
                        if (fromName) setImportReleaseId(fromName);
                      }}
                      required
                    />
                  </label>
                  <label>
                    <span>شناسه نسخه (release ID)</span>
                    <input
                      dir="ltr"
                      className="ltr-isolate"
                      value={importReleaseId}
                      onChange={(e) => setImportReleaseId(e.target.value)}
                      required
                    />
                  </label>
                  <div className="form-hint">
                    <p>
                      مقادیر مرجع{" "}
                      <bdi dir="ltr" className="ltr-isolate">
                        {importFrozenRef?.label ?? "rc.21"}
                      </bdi>{" "}
                      برای تطبیق:
                    </p>
                    <ul>
                      <li>
                        شناسه:{" "}
                        <bdi dir="ltr" className="ltr-isolate">
                          {importFrozenRef?.releaseId}
                        </bdi>
                      </li>
                      <li>
                        SHA-256:{" "}
                        <bdi dir="ltr" className="ltr-isolate">
                          {importFrozenRef?.sha256}
                        </bdi>
                      </li>
                      <li>
                        تعداد فایل:{" "}
                        <bdi dir="ltr" className="ltr-isolate">
                          {importFrozenRef?.fileCount}
                        </bdi>
                      </li>
                    </ul>
                    <p>تأیید باید با همان SHA-256 منجمد همان نسخه باشد — نه هش rc.17.</p>
                  </div>
                  <label>
                    <span>یادداشت (اختیاری)</span>
                    <input value={importNotes} onChange={(e) => setImportNotes(e.target.value)} />
                  </label>
                </ComposerSection>
                <StickyFormActions>
                  <button type="submit" className="btn btn--primary" disabled={importing}>
                    {importing ? c.importing : c.importSubmit}
                  </button>
                </StickyFormActions>
              </form>
            </ComposerPanel>
          ) : null}

          <ComposerPanel className="bundle-composer-panel">
            <ComposerHeader
              kind={cmsStrings.composerChrome.kindBundleGenerate}
              title={c.generateTitle}
              statusLabel={
                verifiedDefault
                  ? cmsStrings.composerChrome.statusCreate
                  : cmsStrings.composerChrome.statusLocked
              }
              statusTone={verifiedDefault ? "info" : "warning"}
            />
            {!verifiedDefault ? (
              <p className="banner banner--warning">{ui.noDefaultWarning}</p>
            ) : (
              <form
                className="content-form content-form--in-card composer-form composer-form--bundle"
                onSubmit={handleGenerate}
              >
                <ComposerSection id="bundle-section-generate" title={c.generateTitle} bare>
                  {verifiedDefault && !verifiedDefault.isDefault ? (
                    <p className="banner banner--warning">
                      {ui.notDefaultHint}{" "}
                      <bdi dir="ltr" className="ltr-isolate latin-digits">
                        {verifiedDefault.releaseId}
                      </bdi>
                    </p>
                  ) : null}
                  <p className="banner banner--warning">
                    CMS هیچ فایلی به روتر میکروتیک آپلود نمی‌کند. پس از دانلود، ZIP را دستی روی روتر
                    همان سایت بارگذاری کنید.
                  </p>
                  <label>
                    <span>سایت</span>
                    <select
                      value={generateSiteId}
                      onChange={(e) => setGenerateSiteId(e.target.value)}
                      required
                    >
                      <option value="">— انتخاب —</option>
                      {generatableSites.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name} ({s.code})
                        </option>
                      ))}
                    </select>
                  </label>
                  {generatableSites.length === 0 && (
                    <div className="form-hint">
                      <p>سایت فعال با کد قفل‌شده یافت نشد.</p>
                      <p>
                        برای ساخت بسته، ابتدا یک <Link to="/announcements">اطلاعیه</Link> یا{" "}
                        <Link to="/promotions">پیشنهاد</Link> را برای همان سایت منتشر کنید — پس از
                        اولین انتشار، کد سایت قفل می‌شود و اینجا ظاهر می‌شود.
                      </p>
                      {unlockedActiveSites.length > 0 && (
                        <p>
                          سایت‌های فعال بدون قفل:{" "}
                          {unlockedActiveSites.map((s) => s.code).join("، ")}
                        </p>
                      )}
                    </div>
                  )}
                  {selectedSite && (
                    <label>
                      <span>کد سایت (فقط خواندنی)</span>
                      <input
                        dir="ltr"
                        className="ltr-isolate"
                        readOnly
                        value={selectedSite.code}
                      />
                    </label>
                  )}
                  <label>
                    <span>الگوی پایه</span>
                    <input
                      dir="ltr"
                      className="ltr-isolate"
                      readOnly
                      value={`${verifiedDefault.releaseId} (${verifiedDefault.fileCount} فایل)`}
                    />
                  </label>
                </ComposerSection>
                <StickyFormActions>
                  <button
                    type="submit"
                    className="btn btn--primary"
                    disabled={generating || !generateSiteId || generatableSites.length === 0}
                  >
                    {generating ? c.generating : c.generateSubmit}
                  </button>
                </StickyFormActions>
              </form>
            )}
          </ComposerPanel>
        </ComposerWorkspace>
      </div>

      {verifyTarget && (
        <div className="modal-backdrop" role="presentation" onClick={() => setVerifyTarget(null)}>
          <div
            className="modal"
            role="dialog"
            aria-labelledby="verify-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="verify-title">تأیید الگو</h2>
            <p>
              فایل «{verifyTarget.sourceFilename}» — SHA سرور:{" "}
              <bdi dir="ltr" className="ltr-isolate">
                {verifyTarget.sha256.slice(0, 12)}…
              </bdi>
            </p>
            {templateReleaseHashConflict(verifyTarget) ? (
              <p className="banner banner--error">
                شناسهٔ ورود با هش منجمد این ZIP هم‌خوان نیست. الگو را بازنشسته کنید. سپس فایل را با
                شناسهٔ درست وارد کنید.
              </p>
            ) : null}
            <form onSubmit={handleVerify}>
              <label>
                <span>شناسه نسخه</span>
                <input
                  dir="ltr"
                  className="ltr-isolate"
                  value={verifyReleaseId}
                  onChange={(e) => setVerifyReleaseId(e.target.value)}
                  required
                />
              </label>
              <label>
                <span>SHA-256 مورد انتظار</span>
                <input
                  dir="ltr"
                  className="ltr-isolate"
                  value={verifySha256}
                  onChange={(e) => setVerifySha256(e.target.value)}
                  required
                  pattern="[a-fA-F0-9]{64}"
                />
              </label>
              <label>
                <span>تعداد فایل</span>
                <input
                  dir="ltr"
                  className="ltr-isolate"
                  type="number"
                  min={1}
                  max={128}
                  value={verifyFileCount}
                  onChange={(e) => setVerifyFileCount(Number(e.target.value))}
                  required
                />
              </label>
              <div className="modal-actions">
                <button type="button" className="btn btn--ghost" onClick={() => setVerifyTarget(null)}>
                  انصراف
                </button>
                <button
                  type="submit"
                  className="btn btn--primary"
                  disabled={verifying || templateReleaseHashConflict(verifyTarget)}
                >
                  {verifying ? "در حال تأیید…" : "تأیید الگو"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {revokeTarget && (
        <div className="modal-backdrop" role="presentation" onClick={() => setRevokeTarget(null)}>
          <div
            className="modal"
            role="dialog"
            aria-labelledby="revoke-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="revoke-title">ابطال بسته</h2>
            <p>
              بسته سایت «{revokeTarget.siteCode}» (شماره {revokeTarget.sequence}) از دانلود حذف
              می‌شود. رکورد ممیزی حفظ می‌شود.
            </p>
            <form onSubmit={handleRevoke}>
              <div className="modal-actions">
                <button type="button" className="btn btn--ghost" onClick={() => setRevokeTarget(null)}>
                  انصراف
                </button>
                <button
                  type="submit"
                  className="btn btn--danger"
                  disabled={revoking}
                  aria-busy={revoking}
                >
                  {revoking ? "در حال ابطال…" : "ابطال بسته"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
